import { useCallback, Dispatch, SetStateAction } from 'react';
import { ActiveView, StorageSection } from '../types';
import { useToast } from '../contexts/ToastContext';

export interface NavigationHandlers {
  navigateToHome: () => void;
  navigateToMaterials: () => void;
  navigateToUsers: () => void;
  navigateToSettings: () => void;
  navigateToQuotes: () => void;
  navigateToUserSettings: () => void;
  navigateToHsm: () => void;
  navigateToBant: () => void;
  navigateToContacts: () => void;
  navigateToStorage: () => void;
  navigateToStorageSection: (section: StorageSection) => void;
  navigateToAnalytics: () => void;
  navigateToAnalyticsSection: (section: 'utm' | 'chapas' | 'medalhas') => void;
  navigateToMedals: () => void;
  navigateToRepescagem: () => void;
  navigateToMetaTargeting: () => void;
  navigateToGoogleTrends: () => void;
}

export interface UseNavigationOptions {
  setActiveView: Dispatch<SetStateAction<ActiveView>>;
  setStorageSection?: Dispatch<SetStateAction<StorageSection>>;
  setAnalyticsSection?: Dispatch<SetStateAction<'utm' | 'chapas' | 'medalhas'>>;
  // Permission flags
  canManageHsm: boolean;
  canManageBant: boolean;
  canManageContacts: boolean;
  canManageStorage: boolean;
  canViewAnalytics: boolean;
  // Data fetching callbacks for views that need immediate data loading
  hsmCallbacks?: {
    fetchContacts: () => Promise<{ success: boolean; error?: string }>;
    fetchCampaignMessages: () => Promise<{ success: boolean; error?: string }>;
    fetchCampaignStats: () => Promise<{ success: boolean; error?: string }>;
    fetchMessageLogs: () => Promise<{ success: boolean; error?: string }>;
    fetchTemplates: () => Promise<{ success: boolean; error?: string }>;
  };
  bantCallbacks?: {
    fetchQualifications: () => Promise<{ success: boolean; error?: string }>;
  };
  contactsCallbacks?: {
    fetchContacts: () => Promise<{ success: boolean; error?: string }>;
  };
  storageCallbacks?: {
    fetchStockItems: () => Promise<{ success: boolean; error?: string }>;
    fetchGroups: () => Promise<{ success: boolean; error?: string }>;
    fetchCustomStatuses: () => Promise<{ success: boolean; error?: string }>;
  };
  analyticsCallbacks?: {
    fetchAnalytics: () => Promise<{ success: boolean; error?: string }>;
  };
}

/**
 * Hook to manage navigation state and handlers for the backoffice application.
 * Extracts the repetitive navigation pattern into reusable handlers.
 */
export function useNavigation(options: UseNavigationOptions): NavigationHandlers {
  const {
    setActiveView,
    setStorageSection,
    setAnalyticsSection,
    canManageHsm,
    canManageBant,
    canManageContacts,
    canManageStorage,
    canViewAnalytics,
    hsmCallbacks,
    bantCallbacks,
    contactsCallbacks,
    storageCallbacks,
    analyticsCallbacks,
  } = options;

  const toast = useToast();

  /**
   * Helper: navigate to a view, clear toasts, and optionally fetch data.
   */
  const navigateWithFetch = useCallback(
    (
      view: ActiveView,
      fetchCallbacks?: (() => Promise<{ success: boolean; error?: string }>)[],
      canFetch?: boolean
    ) => {
      setActiveView(view);
      toast.clear();

      if (fetchCallbacks && canFetch) {
        fetchCallbacks.forEach((fetchFn) => {
          fetchFn().then((result) => {
            if (!result.success && result.error) {
              toast.error(result.error);
            }
          }).catch((err) => {
            console.error(`[Navigation] Error fetching data for ${view}:`, err);
          });
        });
      }
    },
    [setActiveView, toast]
  );

  // Simple navigation handlers (no data fetching)
  const navigateToHome = useCallback(
    () => navigateWithFetch('home'),
    [navigateWithFetch]
  );

  const navigateToMaterials = useCallback(
    () => navigateWithFetch('materials'),
    [navigateWithFetch]
  );

  const navigateToUsers = useCallback(
    () => navigateWithFetch('users'),
    [navigateWithFetch]
  );

  const navigateToSettings = useCallback(
    () => navigateWithFetch('settings'),
    [navigateWithFetch]
  );

  const navigateToQuotes = useCallback(
    () => navigateWithFetch('quotes'),
    [navigateWithFetch]
  );

  const navigateToUserSettings = useCallback(
    () => navigateWithFetch('user-settings'),
    [navigateWithFetch]
  );

  const navigateToMedals = useCallback(
    () => navigateWithFetch('medals'),
    [navigateWithFetch]
  );

  const navigateToRepescagem = useCallback(
    () => navigateWithFetch('repescagem'),
    [navigateWithFetch]
  );

  const navigateToMetaTargeting = useCallback(
    () => navigateWithFetch('meta-targeting'),
    [navigateWithFetch]
  );

  const navigateToGoogleTrends = useCallback(
    () => navigateWithFetch('google-trends'),
    [navigateWithFetch]
  );

  // HSM navigation with data fetching
  const navigateToHsm = useCallback(() => {
    setActiveView('hsm');
    toast.clear();

    if (canManageHsm && hsmCallbacks) {
      hsmCallbacks.fetchContacts().then((result) => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });

      hsmCallbacks.fetchCampaignMessages();
      hsmCallbacks.fetchCampaignStats();
      hsmCallbacks.fetchMessageLogs();
      hsmCallbacks.fetchTemplates();
    }
  }, [setActiveView, toast, canManageHsm, hsmCallbacks]);

  // BANT navigation with data fetching
  const navigateToBant = useCallback(() => {
    setActiveView('bant');
    toast.clear();

    if (canManageBant && bantCallbacks) {
      bantCallbacks.fetchQualifications().then((result) => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }
  }, [setActiveView, toast, canManageBant, bantCallbacks]);

  // Contacts navigation with data fetching
  const navigateToContacts = useCallback(() => {
    setActiveView('contacts');
    toast.clear();

    if (canManageContacts && contactsCallbacks) {
      contactsCallbacks.fetchContacts().then((result) => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }
  }, [setActiveView, toast, canManageContacts, contactsCallbacks]);

  // Storage navigation with data fetching
  const navigateToStorage = useCallback(() => {
    setActiveView('storage');
    toast.clear();

    if (canManageStorage && storageCallbacks) {
      storageCallbacks.fetchStockItems().then((result) => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      storageCallbacks.fetchGroups();
      storageCallbacks.fetchCustomStatuses();
    }
  }, [setActiveView, toast, canManageStorage, storageCallbacks]);

  // Storage section navigation (Entradas, Relatorios, Configuracoes)
  const navigateToStorageSection = useCallback((section: StorageSection) => {
    setActiveView('storage');
    toast.clear();

    if (setStorageSection) {
      setStorageSection(section);
    }

    if (canManageStorage && storageCallbacks) {
      storageCallbacks.fetchStockItems().then((result) => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
      storageCallbacks.fetchGroups();
      storageCallbacks.fetchCustomStatuses();
    }
  }, [setActiveView, toast, setStorageSection, canManageStorage, storageCallbacks]);

  // Analytics navigation with data fetching
  const navigateToAnalytics = useCallback(() => {
    setActiveView('analytics');
    toast.clear();

    if (canViewAnalytics && analyticsCallbacks) {
      analyticsCallbacks.fetchAnalytics().then((result) => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }
  }, [setActiveView, toast, canViewAnalytics, analyticsCallbacks]);

  // Analytics section navigation (UTM, Chapas, Medalhas)
  const navigateToAnalyticsSection = useCallback((section: 'utm' | 'chapas' | 'medalhas') => {
    setActiveView('analytics');
    toast.clear();

    if (setAnalyticsSection) {
      setAnalyticsSection(section);
    }

    // Only fetch UTM analytics data when navigating to UTM section
    if (section === 'utm' && canViewAnalytics && analyticsCallbacks) {
      analyticsCallbacks.fetchAnalytics().then((result) => {
        if (!result.success && result.error) {
          toast.error(result.error);
        }
      });
    }
  }, [setActiveView, toast, setAnalyticsSection, canViewAnalytics, analyticsCallbacks]);

  return {
    navigateToHome,
    navigateToMaterials,
    navigateToUsers,
    navigateToSettings,
    navigateToQuotes,
    navigateToUserSettings,
    navigateToHsm,
    navigateToBant,
    navigateToContacts,
    navigateToStorage,
    navigateToStorageSection,
    navigateToAnalytics,
    navigateToAnalyticsSection,
    navigateToMedals,
    navigateToRepescagem,
    navigateToMetaTargeting,
    navigateToGoogleTrends,
  };
}
